import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

export interface LanguageUrl {
  locale: 'en' | 'fi' | 'sv';
  url: string;
}

export interface LanguageSwitcherProps {
  languageUrls: {
    all: LanguageUrl[];
  };
  canonicalLocale: string;
  originalJobLanguage?: string | null;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  languageUrls,
  canonicalLocale,
  originalJobLanguage,
}) => {
  const intl = useIntl();

  if (!languageUrls || languageUrls.all.length <= 1) {
    return null;
  }

  const localeNames: Record<'en' | 'fi' | 'sv', string> = {
    en: 'English',
    fi: 'Suomi',
    sv: 'Svenska'
  };

  return (
    <nav 
      className="mb-4 px-1 sm:px-0" 
      aria-label={intl.formatMessage({ 
        id: 'jobDetailPage.languageNavigation', 
        defaultMessage: 'Available languages', 
        description: 'Accessibility label for language selector showing available translations of the job' 
      })}
    >
      <div className="flex items-center gap-2 text-sm">
        <span className="text-[#3D3529] dark:text-[#C4A882]">
          <FormattedMessage 
            id="jobDetailPage.availableLanguages" 
            defaultMessage="Available in:" 
            description="Label prefix for list of languages this job is available in" 
          />
        </span>
        <div className="flex items-center gap-2">
          {languageUrls.all.map(({ locale, url }, index) => {
            const isCurrent = locale === canonicalLocale;
            const isOriginal = originalJobLanguage && locale === originalJobLanguage;
            const isLast = index === languageUrls.all.length - 1;

            return (
              <React.Fragment key={locale}>
                {isCurrent ? (
                  <span className="text-[#1E1A14] dark:text-[#F2EADF] font-medium flex items-center gap-1.5">
                    {localeNames[locale]}
                    {isOriginal && (
                      <span className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] italic">
                        (<FormattedMessage 
                          id="jobDetailPage.originalLanguage" 
                          defaultMessage="Original" 
                          description="Label indicating the original language the job was posted in" 
                        />)
                      </span>
                    )}
                  </span>
                ) : (
                  <a
                    href={url}
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = url;
                    }}
                    className="text-[#3B5E45] dark:text-[#98CFA8] hover:text-[#2A4533] dark:hover:text-[#B6D4BC] underline transition-colors flex items-center gap-1.5"
                  >
                    {localeNames[locale]}
                    {isOriginal && (
                      <span className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] italic">
                        (<FormattedMessage 
                          id="jobDetailPage.originalLanguage" 
                          defaultMessage="Original" 
                          description="Label indicating the original language the job was posted in" 
                        />)
                      </span>
                    )}
                  </a>
                )}
                {!isLast && (
                  <span className="text-[#7A6E5F] dark:text-[#8A7060]">•</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
